import { useEffect, useRef, useState, useCallback } from 'react';
import { Search, X, ChevronDown, Loader2, FileText, Filter, Square, CheckSquare, BookPlus, CheckCheck } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useContractsStore } from '@/store/contracts';
import { useAuthStore } from '@/store/auth';
import { getContractDocxBlob } from '@/services/contractsApi';
import { uploadTemplate } from '@/services/api';

const STATUS_OPTIONS = ['', 'Чинний', 'Втратив чинність', 'Не набрав чинності'];

const STATUS_DOT: Record<string, string> = {
  'Чинний': 'bg-emerald-400',
  'Втратив чинність': 'bg-red-400',
  'Втратив чинність частково': 'bg-amber-400',
  'Не набрав чинності': 'bg-gray-400',
};

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: CURRENT_YEAR - 1990 }, (_, i) => CURRENT_YEAR - i);

function extractSubType(typeDocName: string | null): string {
  if (!typeDocName) return '';
  const clean = typeDocName.replace(/[{}]/g, '');
  const parts = clean.split(',').map((s) => s.trim().replace(/"/g, ''));
  const specific = parts.filter((p) => p !== 'Шаблон' && p !== 'Рішення');
  return specific[0] || parts[0] || '';
}

export function ContractsList() {
  const {
    query, setQuery, statusFilter, setStatusFilter, typeFilter, setTypeFilter,
    yearFrom, yearTo, setYearFrom, setYearTo,
    results, browse, loading, hasMore, types,
    activeId, search, loadMore, loadTypes, openContract,
  } = useContractsStore();

  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === 'admin';

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });
  const [bulkDone, setBulkDone] = useState(false);

  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const didInit = useRef(false);

  useEffect(() => {
    if (!didInit.current) {
      didInit.current = true;
      loadTypes();
      search();
    }
  }, [loadTypes, search]);

  const handleQueryChange = useCallback((v: string) => {
    setQuery(v);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => search(), 350);
  }, [setQuery, search]);

  const handleFilterChange = useCallback(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => search(), 200);
  }, [search]);

  // Infinite scroll
  useEffect(() => {
    const el = bottomRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && hasMore && !loading) loadMore();
    }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, loading, loadMore]);

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const clearSelection = () => {
    setSelectedIds(new Set());
    setBulkDone(false);
  };

  const handleAddToTemplates = async () => {
    if (bulkLoading || selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    setBulkLoading(true);
    setBulkProgress({ done: 0, total: ids.length });
    setBulkDone(false);
    let done = 0;
    for (const id of ids) {
      try {
        const { blob, filename } = await getContractDocxBlob(id);
        const file = new File([blob], filename, { type: blob.type });
        await uploadTemplate(file);
      } catch (err) {
        console.error('addToTemplates error for', id, err);
      }
      done++;
      setBulkProgress({ done, total: ids.length });
    }
    setBulkLoading(false);
    setBulkDone(true);
    setTimeout(() => { clearSelection(); }, 2000);
  };

  const items = query.trim().length >= 2 ? results : browse;
  const total = items.length;
  const hasSelection = selectedIds.size > 0;

  const selectStyle = { colorScheme: 'dark' as const };

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Search bar */}
      <div className="px-3 pt-3 pb-2 space-y-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-white/40 pointer-events-none" />
          <input
            value={query}
            onChange={(e) => handleQueryChange(e.target.value)}
            placeholder="Пошук шаблонів та договорів..."
            className="w-full pl-9 pr-8 h-9 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-1 focus:ring-blue-600/50"
          />
          {query && (
            <button onClick={() => handleQueryChange('')} className="absolute right-2 top-2 p-1 text-white/40 hover:text-white transition-colors">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Filter toggle */}
        <button
          onClick={() => setFiltersOpen((v) => !v)}
          className={cn(
            'flex items-center gap-1.5 text-[11px] px-2 py-1 rounded transition-colors',
            (statusFilter || typeFilter || yearFrom || yearTo) ? 'text-blue-400 bg-blue-500/10' : 'text-white/40 hover:text-white/70'
          )}
        >
          <Filter className="w-3 h-3" />
          Фільтри
          {(statusFilter || typeFilter || yearFrom || yearTo) && <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />}
          <ChevronDown className={cn('w-3 h-3 ml-auto transition-transform', filtersOpen && 'rotate-180')} />
        </button>

        {filtersOpen && (
          <div className="space-y-1.5 pt-1">
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); handleFilterChange(); }}
              style={selectStyle}
              className="w-full h-8 px-2 bg-[#1a1a2e] border border-white/10 rounded-lg text-[12px] text-white/80 focus:outline-none focus:ring-1 focus:ring-blue-600/40"
            >
              <option value="">Усі статуси</option>
              {STATUS_OPTIONS.filter(Boolean).map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>

            <select
              value={typeFilter}
              onChange={(e) => { setTypeFilter(e.target.value); handleFilterChange(); }}
              style={selectStyle}
              className="w-full h-8 px-2 bg-[#1a1a2e] border border-white/10 rounded-lg text-[12px] text-white/80 focus:outline-none focus:ring-1 focus:ring-blue-600/40"
            >
              <option value="">Усі типи</option>
              {types.map((t) => (
                <option key={t} value={t}>{extractSubType(t) || t}</option>
              ))}
            </select>

            {/* Year range */}
            <div className="flex items-center gap-1.5">
              <select
                value={yearFrom}
                onChange={(e) => { setYearFrom(e.target.value); handleFilterChange(); }}
                style={selectStyle}
                className="flex-1 h-8 px-2 bg-[#1a1a2e] border border-white/10 rounded-lg text-[12px] text-white/80 focus:outline-none focus:ring-1 focus:ring-blue-600/40"
              >
                <option value="">Від року</option>
                {YEAR_OPTIONS.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
              <span className="text-white/30 text-[11px] shrink-0">—</span>
              <select
                value={yearTo}
                onChange={(e) => { setYearTo(e.target.value); handleFilterChange(); }}
                style={selectStyle}
                className="flex-1 h-8 px-2 bg-[#1a1a2e] border border-white/10 rounded-lg text-[12px] text-white/80 focus:outline-none focus:ring-1 focus:ring-blue-600/40"
              >
                <option value="">До року</option>
                {YEAR_OPTIONS.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Count */}
      {!loading && total > 0 && (
        <div className="px-3 pb-1 flex items-center justify-between">
          <span className="text-[10px] text-white/25 uppercase tracking-widest">
            {total}+ документів
          </span>
          {isAdmin && (
            <button
              onClick={() => setSelectedIds(new Set(items.map((i) => i.id)))}
              className="text-[10px] text-white/30 hover:text-white/60 transition-colors"
            >
              Вибрати всі
            </button>
          )}
        </div>
      )}

      {/* List */}
      <div className="flex-1 overflow-y-auto px-2 space-y-0.5 pb-2 min-h-0 scrollbar-thin scrollbar-thumb-white/10">
        {loading && total === 0 && (
          <div className="flex justify-center py-8">
            <Loader2 className="w-5 h-5 text-white/30 animate-spin" />
          </div>
        )}

        {!loading && total === 0 && (
          <div className="flex flex-col items-center gap-2 py-10 text-white/25">
            <FileText className="w-8 h-8" />
            <p className="text-xs text-center">Нічого не знайдено</p>
          </div>
        )}

        {items.map((item) => {
          const isActive = item.id === activeId;
          const isSelected = selectedIds.has(item.id);
          const subType = extractSubType(item.type_doc_name);
          return (
            <div key={item.id} className="group relative flex items-start gap-1">
              {/* Checkbox — only for admins */}
              {isAdmin && (
                <button
                  onClick={(e) => { e.stopPropagation(); toggleSelect(item.id); }}
                  className={cn(
                    'shrink-0 mt-1 p-0.5 rounded transition-all',
                    hasSelection ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
                    isSelected ? 'text-blue-400' : 'text-white/25 hover:text-white/50'
                  )}
                  title={isSelected ? 'Зняти вибір' : 'Вибрати'}
                >
                  {isSelected
                    ? <CheckSquare className="w-4 h-4" />
                    : <Square className="w-4 h-4" />}
                </button>
              )}

              {/* Item button */}
              <button
                onClick={() => openContract(item.id)}
                className={cn(
                  'flex-1 text-left px-3 py-2.5 rounded-xl border transition-all',
                  isActive
                    ? 'bg-blue-600/15 border-blue-500/30'
                    : isSelected
                    ? 'bg-blue-600/10 border-blue-500/20'
                    : 'border-transparent hover:bg-white/5 hover:border-white/8'
                )}
              >
                <div className="flex items-start gap-2">
                  <div className={cn('w-1.5 h-1.5 rounded-full mt-1.5 shrink-0',
                    STATUS_DOT[item.status_name || ''] || 'bg-white/20')} />
                  <div className="flex-1 min-w-0">
                    <p className={cn('text-[12px] leading-snug line-clamp-2',
                      isActive ? 'text-white' : 'text-white/80')}>
                      {item.title}
                    </p>
                    <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 mt-1">
                      {subType && (
                        <span className="text-[10px] text-white/35">{subType}</span>
                      )}
                      {item.date_accept_iso && (
                        <span className="text-[10px] text-white/25">
                          {new Date(item.date_accept_iso).toLocaleDateString('uk-UA', { year: 'numeric', month: 'short' })}
                        </span>
                      )}
                    </div>
                    {'snippet' in item && item.snippet && (
                      <p className="text-[10px] text-white/35 mt-0.5 line-clamp-1 leading-relaxed">
                        {item.snippet.replace(/«|»/g, '')}
                      </p>
                    )}
                  </div>
                </div>
              </button>
            </div>
          );
        })}

        {/* Infinite scroll trigger */}
        <div ref={bottomRef} className="h-4 flex items-center justify-center">
          {loading && total > 0 && <Loader2 className="w-4 h-4 text-white/20 animate-spin" />}
        </div>
      </div>

      {/* Bulk action bar */}
      {hasSelection && isAdmin && (
        <div className="shrink-0 border-t border-white/10 bg-[var(--t-panel)] p-3 space-y-2">
          <div className="flex items-center justify-between text-[11px]">
            <span className="text-white/50">Вибрано: {selectedIds.size}</span>
            <button onClick={clearSelection} className="text-white/30 hover:text-white/60 transition-colors">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <button
            onClick={handleAddToTemplates}
            disabled={bulkLoading || bulkDone}
            className={cn(
              'w-full flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-[12px] font-medium transition-colors',
              bulkDone
                ? 'bg-emerald-600/20 text-emerald-300 border border-emerald-500/30'
                : 'bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-60'
            )}
          >
            {bulkDone ? (
              <><CheckCheck className="w-3.5 h-3.5" /> Додано до шаблонів</>
            ) : bulkLoading ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Додаю {bulkProgress.done}/{bulkProgress.total}…</>
            ) : (
              <><BookPlus className="w-3.5 h-3.5" /> Додати як шаблони</>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
